import { useState, useEffect, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';

// Extract namespace from Roam page title (duplicated from useSearchEngine for independence)
const extractNamespace = (pageTitle) => {
  if (!pageTitle) return null;
  
  // Handle nested namespaces like "Video/Show/Sherlock" or "Video/Movie/The Half of It"
  const parts = pageTitle.split('/');
  if (parts.length >= 2) {
    // For nested namespaces, take the most specific (last namespace part)
    // e.g., "Video/Show/Sherlock" -> "show", "Video/Movie/The Half of It" -> "movie"
    if (parts.length >= 3 && parts[0].toLowerCase() === 'video') {
      return parts[1].toLowerCase(); // "show" or "movie"
    }
    // For simple namespaces like "Book/Title" or "Article/Title"
    return parts[0].toLowerCase();
  }
  
  return null;
};

/**
 * Custom hook for providing search suggestions for different operators
 * @param {Array} allTweets - All tweets to extract data from
 * @param {string} searchQuery - The current search query
 * @param {number} cursorPosition - Current cursor position in the search input
 * @returns {Object} - Functions and state for search suggestions
 */
const useSearchSuggestions = (allTweets, searchQuery, cursorPosition) => {
  const [isShowingSuggestions, setIsShowingSuggestions] = useState(false);
  const [currentOperator, setCurrentOperator] = useState(null);
  const [suggestionQuery, setSuggestionQuery] = useState('');
  const [preventSuggestions, setPreventSuggestions] = useState(false);

  // Extract unique values from tweets
  const uniqueValues = useMemo(() => {
    if (!allTweets || !allTweets.length) return {};

    // Extract users (for from: operator)
    const userMap = new Map();
    allTweets.forEach(tweet => {
      if (tweet.user) {
        const screenName = tweet.user.screen_name || tweet.user.handle;
        if (screenName && !userMap.has(screenName)) {
          userMap.set(screenName, {
            value: screenName,
            displayName: screenName,
            title: tweet.user.name || '',
            image: tweet.user.profile_image_url || tweet.user.profilePicUrl,
            frequency: 1
          });
        } else if (screenName) {
          // Increment frequency counter
          const user = userMap.get(screenName);
          if (user) {
            user.frequency++;
          }
        }
      }
    });

    // Extract subreddits (for subreddit: operator)
    const subredditMap = new Map();
    allTweets.forEach(tweet => {
      if (tweet.source === 'reddit' && tweet.subreddit) {
        const subreddit = typeof tweet.subreddit === 'string' 
          ? tweet.subreddit 
          : tweet.subreddit.display_name;
        
        if (subreddit && !subredditMap.has(subreddit)) {
          subredditMap.set(subreddit, {
            value: subreddit,
            displayName: subreddit,
            title: `r/${subreddit}`,
            image: null,
            frequency: 1
          });
        } else if (subreddit) {
          const sub = subredditMap.get(subreddit);
          if (sub) {
            sub.frequency++;
          }
        }
      }
    });

    // Extract namespaces (for namespace: operator)
    const namespaceMap = new Map();
    allTweets.forEach(tweet => {
      if (tweet.source === 'roam' && tweet.metadata?.page_title) {
        const namespace = extractNamespace(tweet.metadata.page_title);
        if (namespace && !namespaceMap.has(namespace)) {
          namespaceMap.set(namespace, {
            value: namespace,
            displayName: namespace,
            title: `${namespace.charAt(0).toUpperCase() + namespace.slice(1)} quotes`,
            image: null,
            frequency: 1
          });
        } else if (namespace) {
          const ns = namespaceMap.get(namespace);
          if (ns) {
            ns.frequency++;
          }
        }
      }
    });

    // Create type options (for is: operator)
    const typeOptions = [
      { value: 'like', displayName: 'like', title: 'Liked tweets', icon: 'heart' },
      { value: 'bookmark', displayName: 'bookmark', title: 'Bookmarked tweets', icon: 'bookmark' },
      { value: 'tweet', displayName: 'tweet', title: 'All tweets', icon: 'twitter' },
      { value: 'reddit', displayName: 'reddit', title: 'Reddit posts', icon: 'reddit' },
      { value: 'roam', displayName: 'roam', title: 'Roam quotes', icon: 'roam' }
    ];

    // Create skip category options (for skip: operator)
    const skipOptions = [
      { value: 'manual', displayName: 'manual', title: 'Manually skipped', icon: 'skip' },
      { value: 'low_quality', displayName: 'low_quality', title: 'Low quality content', icon: 'skip' },
      { value: 'image_only', displayName: 'image_only', title: 'Image-only tweets', icon: 'skip' },
      { value: 'long_text', displayName: 'long_text', title: 'Long text tweets', icon: 'skip' },
      { value: 'spam', displayName: 'spam', title: 'Spam content', icon: 'skip' },
      { value: 'duplicate', displayName: 'duplicate', title: 'Duplicate content', icon: 'skip' },
      { value: 'none', displayName: 'none', title: 'Not skipped (exclude all skipped)', icon: 'skip' }
    ];

    // Create has: operator options
    const hasOptions = [
      { value: 'skip', displayName: 'skip', title: 'Has any skip category', icon: 'skip' }
    ];

    return {
      from: Array.from(userMap.values()).sort((a, b) => b.frequency - a.frequency),
      subreddit: Array.from(subredditMap.values()).sort((a, b) => b.frequency - a.frequency),
      is: typeOptions,
      namespace: Array.from(namespaceMap.values()).sort((a, b) => b.frequency - a.frequency),
      skip: skipOptions,
      has: hasOptions
    };
  }, [allTweets]);

  // Check if cursor is in a search operator context
  const operatorContext = useMemo(() => {
    if (preventSuggestions || !searchQuery) return { operator: null, query: '' };
    
    const query = searchQuery;
    const pos = cursorPosition;

    // Check for "from:" operator
    const fromIndex = query.lastIndexOf('from:', pos);
    if (fromIndex !== -1 && fromIndex + 5 <= pos) {
      // Extract text between "from:" and cursor position
      let username = query.substring(fromIndex + 5, pos).trim();
      // Remove quotes if present
      if (username.startsWith('"')) {
        username = username.substring(1);
      }
      
      // Check if we're still within the same term
      const afterOperator = query.substring(fromIndex + 5);
      const nextSpacePos = afterOperator.indexOf(' ');
      const inSameTerm = nextSpacePos === -1 || fromIndex + 5 + nextSpacePos >= pos;
      
      if (inSameTerm) {
        return { operator: 'from', query: username };
      }
    }

    // Check for "subreddit:" operator (with or without parentheses)
    // First try with parentheses
    let subredditMatch = /\(subreddit:([^\)]*)\)/g.exec(query.substring(0, pos));
    if (subredditMatch && subredditMatch.index + subredditMatch[0].length >= pos) {
      const startPosition = subredditMatch.index + '(subreddit:'.length;
      const subName = query.substring(startPosition, pos);
      
      if (pos > startPosition) {
        return { operator: 'subreddit', query: subName };
      }
    }
    
    // Then try without parentheses
    const subredditIndex = query.lastIndexOf('subreddit:', pos);
    if (subredditIndex !== -1 && subredditIndex + 10 <= pos) {
      // Extract text between "subreddit:" and cursor position
      let subreddit = query.substring(subredditIndex + 10, pos).trim();
      // Remove quotes if present
      if (subreddit.startsWith('"')) {
        subreddit = subreddit.substring(1);
      }
      
      // Check if we're still within the same term
      const afterOperator = query.substring(subredditIndex + 10);
      const nextSpacePos = afterOperator.indexOf(' ');
      const inSameTerm = nextSpacePos === -1 || subredditIndex + 10 + nextSpacePos >= pos;
      
      if (inSameTerm) {
        return { operator: 'subreddit', query: subreddit };
      }
    }

    // Check for "is:" operator (with or without parentheses)
    // First try with parentheses
    let isMatch = /\(is:([^\)]*)\)/g.exec(query.substring(0, pos));
    if (isMatch && isMatch.index + isMatch[0].length >= pos) {
      const startPosition = isMatch.index + '(is:'.length;
      const typeName = query.substring(startPosition, pos);
      
      if (pos > startPosition) {
        return { operator: 'is', query: typeName };
      }
    }
    
    // Then try without parentheses
    const isIndex = query.lastIndexOf('is:', pos);
    if (isIndex !== -1 && isIndex + 3 <= pos) {
      // Extract text between "is:" and cursor position
      let type = query.substring(isIndex + 3, pos).trim();
      // Remove quotes if present
      if (type.startsWith('"')) {
        type = type.substring(1);
      }
      
      // Check if we're still within the same term
      const afterOperator = query.substring(isIndex + 3);
      const nextSpacePos = afterOperator.indexOf(' ');
      const inSameTerm = nextSpacePos === -1 || isIndex + 3 + nextSpacePos >= pos;
      
      if (inSameTerm) {
        return { operator: 'is', query: type };
      }
    }

    // Check for "namespace:" operator (with or without parentheses)
    // First try with parentheses
    let namespaceMatch = /\(namespace:([^\)]*)\)/g.exec(query.substring(0, pos));
    if (namespaceMatch && namespaceMatch.index + namespaceMatch[0].length >= pos) {
      const startPosition = namespaceMatch.index + '(namespace:'.length;
      const namespaceName = query.substring(startPosition, pos);

      if (pos > startPosition) {
        return { operator: 'namespace', query: namespaceName };
      }
    }

    // Then try without parentheses
    const namespaceIndex = query.lastIndexOf('namespace:', pos);
    if (namespaceIndex !== -1 && namespaceIndex + 10 <= pos) {
      // Extract text between "namespace:" and cursor position
      let namespace = query.substring(namespaceIndex + 10, pos).trim();
      // Remove quotes if present
      if (namespace.startsWith('"')) {
        namespace = namespace.substring(1);
      }

      // Check if we're still within the same term
      const afterOperator = query.substring(namespaceIndex + 10);
      const nextSpacePos = afterOperator.indexOf(' ');
      const inSameTerm = nextSpacePos === -1 || namespaceIndex + 10 + nextSpacePos >= pos;

      if (inSameTerm) {
        return { operator: 'namespace', query: namespace };
      }
    }

    // Check for "skip:" operator
    const skipIndex = query.lastIndexOf('skip:', pos);
    if (skipIndex !== -1 && skipIndex + 5 <= pos) {
      // Extract text between "skip:" and cursor position
      let skipValue = query.substring(skipIndex + 5, pos).trim();
      // Remove quotes if present
      if (skipValue.startsWith('"')) {
        skipValue = skipValue.substring(1);
      }

      // Check if we're still within the same term
      const afterOperator = query.substring(skipIndex + 5);
      const nextSpacePos = afterOperator.indexOf(' ');
      const inSameTerm = nextSpacePos === -1 || skipIndex + 5 + nextSpacePos >= pos;

      if (inSameTerm) {
        return { operator: 'skip', query: skipValue };
      }
    }

    // Check for "has:" operator
    const hasIndex = query.lastIndexOf('has:', pos);
    if (hasIndex !== -1 && hasIndex + 4 <= pos) {
      // Extract text between "has:" and cursor position
      let hasValue = query.substring(hasIndex + 4, pos).trim();
      // Remove quotes if present
      if (hasValue.startsWith('"')) {
        hasValue = hasValue.substring(1);
      }

      // Check if we're still within the same term
      const afterOperator = query.substring(hasIndex + 4);
      const nextSpacePos = afterOperator.indexOf(' ');
      const inSameTerm = nextSpacePos === -1 || hasIndex + 4 + nextSpacePos >= pos;

      if (inSameTerm) {
        return { operator: 'has', query: hasValue };
      }
    }

    return { operator: null, query: '' };
  }, [searchQuery, cursorPosition, preventSuggestions]);

  // Update suggestion state when typing
  const updateSuggestionState = useCallback(
    debounce(() => {
      setCurrentOperator(operatorContext.operator);
      setSuggestionQuery(operatorContext.query);
      setIsShowingSuggestions(!!operatorContext.operator);
    }, 50),
    [operatorContext]
  );

  // Watch for changes in search query or cursor position
  useEffect(() => {
    // Skip processing for operators being typed
    if (searchQuery.endsWith(':') && !searchQuery.includes(' ')) {
      setIsShowingSuggestions(false);
      return;
    }
    
    updateSuggestionState();
    return () => updateSuggestionState.cancel();
  }, [searchQuery, cursorPosition, updateSuggestionState]);

  // Get suggestions for the current operator and query
  const suggestions = useMemo(() => {
    if (!currentOperator || !uniqueValues[currentOperator]) return [];

    const values = uniqueValues[currentOperator];
    
    // If query is empty, return top suggestions
    if (!suggestionQuery) {
      return values.slice(0, 7);
    }
    
    // Filter by query
    const filteredValues = values.filter(item => 
      item.value.toLowerCase().includes(suggestionQuery.toLowerCase()) ||
      (item.title && item.title.toLowerCase().includes(suggestionQuery.toLowerCase()))
    );
    
    return filteredValues.slice(0, 7);
  }, [currentOperator, suggestionQuery, uniqueValues]);

  // Select a suggestion
  const selectSuggestion = useCallback((suggestion) => {
    if (!currentOperator || !searchQuery) return null;

    const query = searchQuery;
    const pos = cursorPosition;
    
    let newQuery;
    let newPosition;

    if (currentOperator === 'from') {
      // Handle from: operator (no parentheses)
      const fromIndex = query.lastIndexOf('from:', pos);
      if (fromIndex === -1) return null;

      // Determine if we need quotes (if value has spaces)
      const needsQuotes = suggestion.value.includes(' ');
      const value = needsQuotes ? `"${suggestion.value}"` : suggestion.value;

      // Check if we're already in a quoted context
      const afterFrom = query.substring(fromIndex + 5, pos).trim();
      const inQuotes = afterFrom.startsWith('"');

      if (inQuotes) {
        // Replace everything between from: and the cursor or next quote
        const endQuotePos = query.indexOf('"', fromIndex + 6);
        if (endQuotePos !== -1 && endQuotePos < pos) {
          // Quote is closed before cursor, replace the whole quoted string
          newQuery =
            query.substring(0, fromIndex + 5) +
            value +
            ' ' +
            query.substring(endQuotePos + 1);
          newPosition = fromIndex + 5 + value.length + 1; // +1 for the space
        } else {
          // Find the next space after cursor or end of string
          const nextSpacePos = query.indexOf(' ', pos);
          const replaceEnd = nextSpacePos !== -1 ? nextSpacePos : query.length;
          newQuery =
            query.substring(0, fromIndex + 5) +
            value +
            ' ' +
            query.substring(replaceEnd);
          newPosition = fromIndex + 5 + value.length + 1; // +1 for the space
        }
      } else {
        // Find the next space after from: or end of string
        const nextSpacePos = query.indexOf(' ', fromIndex + 5);
        const replaceEnd = nextSpacePos !== -1 ? nextSpacePos : query.length;
        newQuery =
          query.substring(0, fromIndex + 5) +
          value +
          ' ' +
          query.substring(replaceEnd);
        newPosition = fromIndex + 5 + value.length + 1; // +1 for the space
      }
    } else {
      // Check if we have parentheses
      const hasParentheses = query.includes(`(${currentOperator}:`);
      
      if (hasParentheses) {
        // Handle operators that use parentheses
        const searchPattern = currentOperator === 'is' 
          ? /\(is:[^\)]*\)/g 
          : /\(subreddit:[^\)]*\)/g;
        
        const match = searchPattern.exec(query);
        if (match) {
          // Replace existing operator
          newQuery = query.substring(0, match.index) +
                    `(${currentOperator}:${suggestion.value})` +
                    query.substring(match.index + match[0].length);
          newPosition = match.index + `(${currentOperator}:${suggestion.value})`.length;
        } else {
          // This shouldn't happen normally, but handle it anyway
          newQuery = query.substring(0, pos) +
                    `(${currentOperator}:${suggestion.value})` +
                    query.substring(pos);
          newPosition = pos + `(${currentOperator}:${suggestion.value})`.length;
        }
      } else {
        // Handle operators without parentheses
        const opIndex = query.lastIndexOf(`${currentOperator}:`, pos);
        if (opIndex === -1) return null;
        
        // Determine operator length
        const opLength = currentOperator === 'is' ? 3 :
                        currentOperator === 'subreddit' ? 10 :
                        currentOperator === 'namespace' ? 10 :
                        currentOperator === 'skip' ? 5 :
                        currentOperator === 'has' ? 4 : 0;
        
        // Determine if we need quotes (if value has spaces)
        const needsQuotes = suggestion.value.includes(' ');
        const value = needsQuotes ? `"${suggestion.value}"` : suggestion.value;

        // Check if we're already in a quoted context
        const afterOp = query.substring(opIndex + opLength, pos).trim();
        const inQuotes = afterOp.startsWith('"');

        if (inQuotes) {
          // Replace everything between operator and the cursor or next quote
          const endQuotePos = query.indexOf('"', opIndex + opLength + 1);
          if (endQuotePos !== -1 && endQuotePos < pos) {
            // Quote is closed before cursor, replace the whole quoted string
            newQuery =
              query.substring(0, opIndex + opLength) +
              value +
              ' ' +
              query.substring(endQuotePos + 1);
            newPosition = opIndex + opLength + value.length + 1; // +1 for the space
          } else {
            // Find the next space after cursor or end of string
            const nextSpacePos = query.indexOf(' ', pos);
            const replaceEnd = nextSpacePos !== -1 ? nextSpacePos : query.length;
            newQuery =
              query.substring(0, opIndex + opLength) +
              value +
              ' ' +
              query.substring(replaceEnd);
            newPosition = opIndex + opLength + value.length + 1; // +1 for the space
          }
        } else {
          // Find the next space after operator or end of string
          const nextSpacePos = query.indexOf(' ', opIndex + opLength);
          const replaceEnd = nextSpacePos !== -1 ? nextSpacePos : query.length;
          newQuery =
            query.substring(0, opIndex + opLength) +
            value +
            ' ' +
            query.substring(replaceEnd);
          newPosition = opIndex + opLength + value.length + 1; // +1 for the space
        }
      }
    }

    // Hide suggestions and prevent them from showing immediately
    setIsShowingSuggestions(false);
    setPreventSuggestions(true);

    // Reset the prevention after a short delay
    setTimeout(() => {
      setPreventSuggestions(false);
    }, 300);

    // Return the new query and cursor position
    return { newQuery, newPosition };
  }, [searchQuery, cursorPosition, currentOperator]);

  // Hide suggestions
  const hideSuggestions = useCallback(() => {
    setIsShowingSuggestions(false);
  }, []);

  // Check if we just typed an operator
  useEffect(() => {
    // Auto-show suggestions when typing an operator
    const operators = [
      { pattern: 'from:', needsSpace: false },
      { pattern: 'is:', needsSpace: false },
      { pattern: 'subreddit:', needsSpace: false },
      { pattern: 'namespace:', needsSpace: false },
      { pattern: 'skip:', needsSpace: false },
      { pattern: 'has:', needsSpace: false },
      { pattern: '(is:', needsSpace: false },
      { pattern: '(subreddit:', needsSpace: false },
      { pattern: '(namespace:', needsSpace: false }
    ];
    
    for (const op of operators) {
      if (searchQuery.endsWith(op.pattern)) {
        setCurrentOperator(op.pattern.replace('(', '').replace(':', ''));
        setSuggestionQuery('');
        setIsShowingSuggestions(true);
        break;
      }
    }
  }, [searchQuery]);

  return {
    isShowingSuggestions,
    currentOperator,
    suggestionQuery,
    suggestions,
    selectSuggestion,
    hideSuggestions
  };
};

export default useSearchSuggestions;